/** SUNRISE beside the surface warp row: names the instant the drawn sun next clears the horizon and hands it to the sim's warp ladder as a `WarpGoal`. */

import type { AtlasState } from '../atlas';
import { simApi } from '../sim/api.ts';
import type { WarpGoal } from '../sim/warp.ts';
import { gameClock } from '../world/clock.ts';
import type { InjectsHandle } from './injects.ts';
import { sunAngles } from './plotField.ts';
import type { PlotSunHandle } from './plotSun.ts';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { ViewClockHandle } from './viewClock.ts';
import type { WarpRowGoal } from './warpRow.ts';

/** Where the run stops: the drawn sun's altitude, degrees. */
const AIM_DEG = 0.5;
/** How far past the aim one frame may carry the sun, degrees. */
const BAND_DEG = 0.5;
/** The search's reach, simulated seconds; a sunrise further off is refused. */
export const SUNRISE_CAP_S = 16 * 86400;
/** Search steps per local day, and at most this many steps in all. */
const STEPS_PER_DAY = 96;
const MAX_STEPS = 4096;
/** A sample older than this, real milliseconds, is not the drawn sun any more. */
const FRESH_MS = 250;
/** Idle, the search reruns at most this often, real milliseconds; during a run, every frame. */
const IDLE_SEARCH_MS = 200;
const RAD = Math.PI / 180;

/** The drawn sun and how its season drifts, all the search needs. */
export interface SunriseSky {
  latDeg: number;
  decDeg: number;
  /** Hour angle, radians; negative is morning. */
  hourAngleRad: number;
  solDayS: number;
  lsDeg: number;
  obliquityDeg: number;
  /** Degrees of Ls per simulated second; 0 where the season does not move. */
  lsRateDegS: number;
}

export type Rise =
  | { kind: 'in'; s: number; slackS: number }
  | { kind: 'polar-night' }
  | { kind: 'midnight-sun' }
  | { kind: 'late'; s: number | null };

/** The next time the sun climbs through `aimDeg` within `capS`; declination follows Ls, offset so the search starts on the drawn sun. */
export function nextSunrise(sky: SunriseSky, aimDeg: number, capS: number): Rise {
  const sinObl = Math.sin(sky.obliquityDeg * RAD);
  const decLs = (ls: number) => Math.asin(Math.sin(ls * RAD) * sinObl) / RAD;
  const dec0 = decLs(sky.lsDeg);
  const altAt = (t: number) => {
    const dec = sky.decDeg + (sky.lsRateDegS === 0 ? 0 : decLs(sky.lsDeg + sky.lsRateDegS * t) - dec0);
    return sunAngles(sky.latDeg, dec, sky.hourAngleRad + (2 * Math.PI * t) / sky.solDayS).alt / RAD;
  };
  const step = Math.max(sky.solDayS / STEPS_PER_DAY, capS / MAX_STEPS);
  let prevT = 0;
  let prevAlt = altAt(0);
  let sawBelow = prevAlt < aimDeg;
  let sawAbove = !sawBelow;
  for (let t = step; t <= capS; t += step) {
    const alt = altAt(t);
    if (prevAlt < aimDeg && alt >= aimDeg) {
      let lo = prevT;
      let hi = t;
      for (let i = 0; i < 40; i++) {
        const mid = (lo + hi) / 2;
        if (altAt(mid) >= aimDeg) hi = mid; else lo = mid;
      }
      const h = Math.min(step / 16, 60);
      const slopeDegS = (altAt(hi + h) - altAt(Math.max(0, hi - h))) / (hi + h - Math.max(0, hi - h));
      return { kind: 'in', s: hi, slackS: Math.min(sky.solDayS / 24, BAND_DEG / Math.max(1e-12, slopeDegS)) };
    }
    if (alt < aimDeg) sawBelow = true; else sawAbove = true;
    prevT = t;
    prevAlt = alt;
  }
  // Past the cap: the drawn declination says whether this is a day or night that ends, or a season's.
  const lat = sky.latDeg * RAD;
  const dec = sky.decDeg * RAD;
  const cosHa = (Math.sin(aimDeg * RAD) - Math.sin(lat) * Math.sin(dec)) / Math.max(1e-9, Math.cos(lat) * Math.cos(dec));
  if (cosHa < -1) return sawBelow ? { kind: 'late', s: null } : { kind: 'midnight-sun' };
  if (cosHa > 1) return sawAbove ? { kind: 'late', s: null } : { kind: 'polar-night' };
  const turns = (-Math.acos(Math.max(-1, cosHa)) - sky.hourAngleRad) / (2 * Math.PI);
  return { kind: 'late', s: (turns - Math.floor(turns)) * sky.solDayS };
}

/** What the button says for a sunrise it will not run to, or null for one it will. */
export function riseRefusal(rise: Rise, capS: number): string | null {
  const capDays = Math.round(capS / 86400);
  switch (rise.kind) {
    case 'in': return null;
    case 'polar-night': return `POLAR NIGHT: NO SUNRISE IN ${capDays} DAYS`;
    case 'midnight-sun': return 'MIDNIGHT SUN: IT DOES NOT SET';
    case 'late': return rise.s === null
      ? `NO SUNRISE IN ${capDays} DAYS`
      : `NEXT SUNRISE IN ${Math.ceil(rise.s / 86400)} DAYS, PAST THE ${capDays}-DAY CAP`;
  }
}

export interface SunriseWarpDeps {
  readonly state: AtlasState;
  readonly solarScrub: SolarScrubHandle;
  readonly plotSun: PlotSunHandle;
  readonly viewClockHandle: ViewClockHandle;
  readonly injects: InjectsHandle;
}

interface Sample {
  sky: SunriseSky;
  altDeg: number;
  t: number;
  ms: number;
  bodyId: string | null;
  rise: Rise;
  /** The clock the rise was searched at, and when, real milliseconds. */
  riseT: number;
  searchMs: number;
}

interface Plan {
  readonly bodyId: string | null;
  readonly pressT: number;
  aimT: number;
  slackS: number;
  /** The drawn sun has been under the aim since the press: a rise from here is the one asked for. */
  sawBelow: boolean;
}

export interface SunriseWarpHandle {
  /** Read the sun `applySun` just placed on the live clock. */
  sample(): void;
  readonly button: WarpRowGoal;
  readonly debug: unknown;
}

export function createSunriseWarp(deps: SunriseWarpDeps): SunriseWarpHandle {
  const { state, solarScrub, plotSun, viewClockHandle } = deps;
  const { SUNRISE_NOON } = deps.injects.state;
  let last: Sample | null = null;
  let run: { goal: WarpGoal; plan: Plan } | null = null;
  let arrived: { t: number; altDeg: number | null; pressT: number } | null = null;

  function aimDeg(sky: SunriseSky): number {
    // `atlas-clock-check --leg surface --inject sunrise-noon`: the run aimed a quarter degree under noon.
    return SUNRISE_NOON ? 90 - Math.abs(sky.latDeg - sky.decDeg) - 0.25 : AIM_DEG;
  }

  function fresh(): Sample | null {
    return last !== null && performance.now() - last.ms <= FRESH_MS && last.bodyId === state.bodyId ? last : null;
  }

  function previewUp(): boolean {
    const v = viewClockHandle.state;
    return v.previewT !== null || v.playing !== null || v.forecasting;
  }

  function running(): boolean {
    return run !== null && simApi.warp.goal() === run.goal;
  }

  /** The searched rise as seen from the clock now. */
  function riseNow(f: Sample): Rise {
    if (f.rise.kind !== 'in') return f.rise;
    return { ...f.rise, s: f.rise.s - (gameClock().t - f.riseT) };
  }

  function sample(): void {
    if (previewUp()) return;
    const p = plotSun.placed;
    const solDayS = solarScrub.bodySolarDayS();
    if (!p || solDayS === null || !(solDayS > 0)) { last = null; return; }
    const sky: SunriseSky = {
      latDeg: p.latDeg, decDeg: p.decDeg, hourAngleRad: p.hourAngleRad, solDayS,
      lsDeg: p.lsDeg, obliquityDeg: p.obliquityDeg,
      lsRateDegS: solarScrub.seasonMoves() ? 360 / solarScrub.bodyYearS() : 0,
    };
    const t = gameClock().t;
    const ms = performance.now();
    const prev = run === null && last !== null && last.bodyId === state.bodyId
      && ms - last.searchMs < IDLE_SEARCH_MS ? last : null;
    last = {
      sky, altDeg: p.altDeg, t, ms, bodyId: state.bodyId,
      rise: prev ? prev.rise : nextSunrise(sky, aimDeg(sky), SUNRISE_CAP_S),
      riseT: prev ? prev.riseT : t,
      searchMs: prev ? prev.searchMs : ms,
    };
    if (run !== null && p.altDeg < aimDeg(sky)) run.plan.sawBelow = true;
    if (run !== null && !running()) {
      arrived = { t, altDeg: p.altDeg, pressT: run.plan.pressT };
      run = null;
    }
  }

  function reason(): string | null {
    if (running()) return null;
    if (previewUp()) return 'CLOSE THE PREVIEW TO WARP TO SUNRISE';
    const f = fresh();
    if (!f) return 'NO SUN HERE';
    const refused = riseRefusal(f.rise, SUNRISE_CAP_S);
    if (refused !== null) return refused;
    return simApi.warp.reachable(1) ? null : (simApi.warp.reason(1) ?? 'LOCKED');
  }

  function press(): string | null {
    if (running()) { simApi.warp.setPick(0); run = null; return null; }
    const why = reason();
    if (why !== null) return why;
    const f = fresh();
    const rise = f ? riseNow(f) : null;
    if (!f || !rise || rise.kind !== 'in') return 'NO SUN HERE';
    const t0 = gameClock().t;
    const plan: Plan = {
      bodyId: state.bodyId, pressT: t0, aimT: t0 + Math.max(0, rise.s), slackS: rise.slackS,
      sawBelow: f.altDeg < aimDeg(f.sky),
    };
    // With no drawn sun the last prediction stands only under a preview; off the surface the run ends.
    const goal: WarpGoal = {
      runningLine: 'WARPING TO SUNRISE',
      arrivedLine: 'SUNRISE',
      remainingS: () => {
        if (state.bodyId !== plan.bodyId) return null;
        const g = fresh();
        if (!g) return previewUp() ? Math.max(0, plan.aimT - gameClock().t) : null;
        const p = riseNow(g);
        if (p.kind !== 'in') return null;
        plan.aimT = gameClock().t + Math.max(0, p.s);
        plan.slackS = p.slackS;
        return Math.max(0, p.s);
      },
      slackS: () => plan.slackS,
      reached: () => {
        const g = fresh();
        return g ? plan.sawBelow && g.altDeg >= aimDeg(g.sky) : gameClock().t >= plan.aimT;
      },
    };
    const refused = simApi.warp.runTo(goal);
    if (refused !== null) return refused;
    run = { goal, plan };
    arrived = null;
    return null;
  }

  const button: WarpRowGoal = {
    id: 'atlas-sunrise',
    label: 'SUNRISE',
    title: 'Warp to the next sunrise here',
    visible: () => state.tier >= 5 && simApi.warp.levels().length > 0,
    reason,
    active: running,
    press,
  };

  return {
    sample,
    button,
    get debug(): unknown {
      const f = fresh();
      const rise = f ? riseNow(f) : null;
      return {
        visible: button.visible(),
        reason: reason(),
        running: running(),
        aimDeg: f ? aimDeg(f.sky) : null,
        fresh: f !== null,
        sun: f ? { altDeg: f.altDeg, ...f.sky } : null,
        rise: rise && rise.kind === 'in' ? { inS: rise.s, slackS: rise.slackS } : (rise ? rise.kind : null),
        pressT: run?.plan.pressT ?? null,
        aimT: run?.plan.aimT ?? null,
        arrived,
        capS: SUNRISE_CAP_S,
      };
    },
  };
}
